import type { FluentCartModalContext } from './fluentcart-modal-context';

const DONATE_BUTTON_SELECTOR = 'a.pifster-donate-btn[data-charity-id]';
const VOTE_BUTTON_SELECTOR = '.js-vote-button, [data-pifster-vote]';
const GIFT_BUTTON_SELECTOR = '[data-pifster-gift]';
const CORPORATE_BUTTON_SELECTOR = '[data-pifster-corporate]';
const CARD_SELECTOR = '[data-instance-id], .monthly-charity, .pifster-charity-card, [data-charity-post-id]';
type TriggerResult = void | PromiseLike<void>;

let voteRequestInFlight = false;

function readNullableNumber(value: string | null): number | null {
  if (value === null || value.trim() === '') {
    return null;
  }

  const parsed = Number.parseInt(value, 10);
  return Number.isNaN(parsed) ? null : parsed;
}

function readNullableFloat(value: string | null): number | null {
  if (value === null || value.trim() === '') {
    return null;
  }

  const parsed = Number.parseFloat(value);
  return Number.isNaN(parsed) ? null : parsed;
}

function getCardElement(trigger: Element): HTMLElement | null {
  const card = trigger.closest(CARD_SELECTOR);
  return card instanceof HTMLElement ? card : null;
}

function getCardTitle(card: HTMLElement | null): string {
  if (!card) {
    return '';
  }

  const titleElement = card.querySelector<HTMLElement>('.monthly-charity-title');
  return titleElement?.textContent?.trim() ?? '';
}

function resolveCardDonateHref(card: HTMLElement | null): string {
  if (!card) {
    return '';
  }

  const donateAnchor = card.querySelector<HTMLAnchorElement>('a.pifster-donate-btn, a.monthly-charity-donate-btn, a[href*="checkout"], a[href*="pabbly"]');
  if (!donateAnchor) {
    return '';
  }

  return donateAnchor.getAttribute('href') ?? '';
}

function buildContextFromDonateAnchor(anchor: HTMLElement): FluentCartModalContext {
  const card = getCardElement(anchor);
  const charityId = readNullableNumber(anchor.getAttribute('data-charity-id'))
    ?? readNullableNumber(card?.getAttribute('data-charity-post-id') ?? null);
  if (charityId === null) {
    throw new Error('FluentCart donate trigger is missing a charity ID.');
  }

  const periodId = readNullableNumber(anchor.getAttribute('data-period-id'));
  const charityName = anchor.getAttribute('data-charity-name')?.trim()
    || getCardTitle(card)
    || '';
  const ratio = readNullableFloat(anchor.getAttribute('data-donation-vote-ratio'))
    ?? readNullableFloat(card?.getAttribute('data-donation-vote-ratio') ?? null);
  const cap = readNullableNumber(anchor.getAttribute('data-bonus-vote-cap'))
    ?? readNullableNumber(card?.getAttribute('data-bonus-vote-cap') ?? null);
  const source = anchor.getAttribute('data-pifster-source') ?? 'donate';
  const entry = anchor.getAttribute('data-pifster-entry')
    || card?.getAttribute('data-pifster-entry')
    || 'standard';

  return {
    charityId,
    charityName,
    periodId,
    intentToken: null,
    beneficiary: null,
    donateHrefFallback: anchor.getAttribute('href') ?? resolveCardDonateHref(card),
    bonusRatio: ratio,
    bonusCap: cap,
    mode: source === 'vote' ? 'vote_plus_donate' : 'donate_only',
    entry,
  };
}

function buildContextFromVoteButton(button: HTMLElement): FluentCartModalContext {
  const card = getCardElement(button);
  const charityId = readNullableNumber(button.getAttribute('data-charity-id'))
    ?? readNullableNumber(card?.getAttribute('data-charity-post-id') ?? null);
  if (charityId === null) {
    throw new Error('FluentCart vote trigger is missing a charity ID.');
  }

  const periodId = readNullableNumber(button.getAttribute('data-period-id'))
    ?? readNullableNumber(card?.getAttribute('data-period-id') ?? null);
  const charityName = button.getAttribute('data-charity-name')?.trim()
    || getCardTitle(card)
    || '';
  const ratio = readNullableFloat(button.getAttribute('data-donation-vote-ratio'))
    ?? readNullableFloat(card?.getAttribute('data-donation-vote-ratio') ?? null);
  const cap = readNullableNumber(button.getAttribute('data-bonus-vote-cap'))
    ?? readNullableNumber(card?.getAttribute('data-bonus-vote-cap') ?? null);

  return {
    charityId,
    charityName,
    periodId,
    intentToken: null,
    beneficiary: null,
    donateHrefFallback: resolveCardDonateHref(card),
    bonusRatio: ratio,
    bonusCap: cap,
    mode: 'vote_plus_donate',
    entry: 'vote_button',
  };
}

function buildGiftPurchaseContext(): FluentCartModalContext {
  return {
    charityId: null,
    charityName: '',
    periodId: null,
    intentToken: null,
    beneficiary: null,
    donateHrefFallback: '',
    bonusRatio: null,
    bonusCap: null,
    mode: 'donate_only',
    entry: 'gift_purchase',
    isGift: true,
  };
}

function buildCorporateSponsorshipContext(): FluentCartModalContext {
  return {
    charityId: null,
    charityName: '',
    periodId: null,
    intentToken: null,
    beneficiary: null,
    donateHrefFallback: '',
    bonusRatio: null,
    bonusCap: null,
    mode: 'corporate_sponsorship',
    entry: 'corporate_checkout',
  };
}

function invokeDonateTrigger(
  onTrigger: (context: FluentCartModalContext) => TriggerResult,
  buildContext: () => FluentCartModalContext,
): void {
  try {
    void Promise.resolve(onTrigger(buildContext())).catch((error: unknown) => {
      console.error('[FluentCart] Donate trigger failed', error);
    });
  } catch (error: unknown) {
    console.error('[FluentCart] Failed to build donate context', error);
  }
}

export function bindDonateTriggers(onTrigger: (context: FluentCartModalContext) => TriggerResult): void {
  document.addEventListener('click', (event: MouseEvent): void => {
    const target = event.target;
    if (!(target instanceof Element)) {
      return;
    }

    const giftButton = target.closest(GIFT_BUTTON_SELECTOR);
    if (giftButton instanceof HTMLElement) {
      event.preventDefault();
      invokeDonateTrigger(onTrigger, buildGiftPurchaseContext);
      return;
    }

    const corporateButton = target.closest(CORPORATE_BUTTON_SELECTOR);
    if (corporateButton instanceof HTMLElement) {
      event.preventDefault();
      invokeDonateTrigger(onTrigger, buildCorporateSponsorshipContext);
      return;
    }

    const donateAnchor = target.closest(DONATE_BUTTON_SELECTOR);
    if (donateAnchor instanceof HTMLElement) {
      event.preventDefault();
      invokeDonateTrigger(onTrigger, () => buildContextFromDonateAnchor(donateAnchor));
    }
  });
}

export function bindVoteTriggers(onTrigger: (context: FluentCartModalContext) => TriggerResult): void {
  document.addEventListener('click', (event: MouseEvent): void => {
    const target = event.target;
    if (!(target instanceof Element)) {
      return;
    }

    const voteButton = target.closest(VOTE_BUTTON_SELECTOR);
    if (!(voteButton instanceof HTMLElement)) {
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    if (voteRequestInFlight || voteButton.dataset['pifsterFluentcartPending'] === '1') {
      return;
    }

    voteRequestInFlight = true;
    const button = voteButton instanceof HTMLButtonElement ? voteButton : null;
    const originalContent = voteButton.innerHTML;
    const originalAriaDisabled = voteButton.getAttribute('aria-disabled');
    const originalDisabled = button?.disabled ?? false;

    voteButton.dataset['pifsterFluentcartHandled'] = '1';
    voteButton.dataset['pifsterFluentcartPending'] = '1';
    voteButton.setAttribute('aria-busy', 'true');
    voteButton.setAttribute('aria-disabled', 'true');
    voteButton.classList.add('is-loading');
    voteButton.textContent = 'Loading…';
    if (button) {
      button.disabled = true;
    }

    const restoreButton = (): void => {
      delete voteButton.dataset['pifsterFluentcartPending'];
      voteButton.classList.remove('is-loading');
      voteButton.removeAttribute('aria-busy');
      if (originalAriaDisabled === null) {
        voteButton.removeAttribute('aria-disabled');
      } else {
        voteButton.setAttribute('aria-disabled', originalAriaDisabled);
      }
      voteButton.innerHTML = originalContent;
      if (button) {
        button.disabled = originalDisabled;
      }
      voteRequestInFlight = false;
    };

    try {
      const result = onTrigger(buildContextFromVoteButton(voteButton));
      void Promise.resolve(result)
        .catch((error: unknown) => {
          console.error('[FluentCart] Vote trigger failed', error);
        })
        .finally(restoreButton);
    } catch (error) {
      console.error('[FluentCart] Failed to build vote context', error);
      restoreButton();
    }
  });
}
